/**
 * imageConverter.js
 * Reusable utility for converting any browser-loaded image to WebP
 * under a specified target size (default 35 KB) using the Canvas API.
 *
 * No external dependencies — uses only the native browser Canvas + Blob APIs.
 *
 * Usage:
 *   import { convertToWebP } from 'modules/utils/imageConverter';
 *   const { file, dataUrl } = await convertToWebP(rawFile);
 */

const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
  'image/tiff',
];

/**
 * Estimates the byte-size of a base-64 encoded data URL.
 * @param {string} dataUrl
 * @returns {number}
 */
function estimateBase64Size(dataUrl) {
  const base64 = dataUrl.split(',')[1] ?? '';
  const padding = (base64.match(/=+$/) ?? [''])[0].length;
  return Math.ceil((base64.length * 3) / 4) - padding;
}

/**
 * Draws an image onto a canvas at the given scale and binary-searches
 * for the highest WebP quality that keeps the output under targetBytes.
 *
 * @param {HTMLImageElement} img
 * @param {number} scale   0 < scale <= 1
 * @param {number} targetBytes
 * @returns {{ dataUrl: string, quality: number } | null}
 */
function tryAtScale(img, scale, targetBytes) {
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);

  let low = 0.01;
  let high = 0.92;
  let bestResult = null;

  // 14 iterations gives quality precision of ~0.00006
  for (let i = 0; i < 14; i++) {
    const quality = (low + high) / 2;
    const dataUrl = canvas.toDataURL('image/webp', quality);
    const size = estimateBase64Size(dataUrl);

    if (size <= targetBytes) {
      bestResult = { dataUrl, quality };
      low = quality; // try to push quality higher
    } else {
      high = quality; // must reduce quality
    }
  }

  return bestResult;
}

/**
 * Converts a browser File/Blob to a WebP File whose encoded size is at or
 * below `targetSizeKB` kilobytes.
 *
 * Strategy:
 *  1. Try full resolution; binary-search WebP quality.
 *  2. If that isn't small enough, progressively scale dimensions down by 10 %
 *     until the image fits (down to 10 % of original size).
 *
 * @param {File|Blob} file         The source image file.
 * @param {number}    targetSizeKB Max output size in KB (default 35).
 * @returns {Promise<{ file: File, dataUrl: string, originalSize: number, convertedSize: number }>}
 */
export function convertToWebP(file, targetSizeKB = 35) {
  return new Promise((resolve, reject) => {
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      reject(new Error(`Unsupported image type: ${file.type}`));
      return;
    }

    const targetBytes = targetSizeKB * 1024;
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let result = null;
      let scale = 1.0;

      while (!result && scale >= 0.09) {
        result = tryAtScale(img, scale, targetBytes);
        if (!result) scale = Math.round((scale - 0.1) * 100) / 100;
      }

      // Last-resort: absolute minimum quality at 10 % scale
      if (!result) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.naturalWidth * 0.1));
        canvas.height = Math.max(1, Math.round(img.naturalHeight * 0.1));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        result = { dataUrl: canvas.toDataURL('image/webp', 0.01) };
      }

      const { dataUrl } = result;
      const base64 = dataUrl.split(',')[1];
      const byteString = atob(base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([ab], { type: 'image/webp' });
      const baseName = file.name.replace(/\.[^.]+$/, '');
      const webpFile = new File([blob], `${baseName}.webp`, {
        type: 'image/webp',
        lastModified: Date.now(),
      });

      resolve({
        file: webpFile,
        dataUrl,
        originalSize: file.size,
        convertedSize: webpFile.size,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    img.src = objectUrl;
  });
}

/**
 * Validates an image File before conversion.
 *
 * @param {File}   file
 * @param {number} maxSizeMB  Maximum allowed source file size in MB (default 5).
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateImageFile(file, maxSizeMB = 5) {
  if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
    return { valid: false, error: `"${file.name}": Unsupported file type.` };
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    return { valid: false, error: `"${file.name}" exceeds the ${maxSizeMB} MB size limit.` };
  }
  return { valid: true, error: null };
}
